<script lang="ts" setup generic="T extends GenericListItemType">
import { GenericListHeading, GenericListItemType } from '@/core/models/generic-list.interface';
import i18n from '@/i18n';
import { deepEqual, getPropertyByKey } from '@nxtpeople/nxt-vue-library';
import { PropType } from 'vue';
import GenericListFilters from './GenericListFilters.vue';
import GenericListHeadings from './GenericListHeadings.vue';
import GenericListItem from './GenericListItem.vue';

const AsyncNxtTable = defineAsyncComponent(async () => {
   return (await import('@nxtpeople/nxt-styles')).NxtTable;
});

const { t } = useI18n();

//#region Props,Emits & Slotsà
const emits = defineEmits<{ (e: 'update:headings', headinsg: Array<GenericListHeading<T>>): void; (e: 'lazy-load'): void }>();
const slots = useSlots();
const props = defineProps({
   query: { type: Object, default: null },
   getIsSelectable: { type: Function as PropType<(item: T) => boolean>, default: null },
   hasPaging: { type: Boolean, default: true },
   hasResetFiltersButton: { type: Boolean, default: true },
   headings: { type: Array as PropType<Array<GenericListHeading<T>>>, default: () => [] },
   advancedHeadings: { type: Boolean, default: false },
   listItems: { type: Array as PropType<Array<T>>, default: () => [] },
   listItemPadding: { type: String as PropType<'var(--nxt-gutter)' | 'var(--nxt-gutter-small)'>, default: 'var(--nxt-gutter)' },
   loading: { type: Boolean, default: false },
   loadingRowCount: { type: Number, default: 25 },
   meta: { type: Object, default: () => ({}) },
   name: { type: String, required: true },
   onFilter: { type: Function as PropType<(payload: { key: string; value: any }) => unknown>, default: null },
   onNavigateToDetails: { type: Function as PropType<(item: any) => unknown>, default: null },
   onResetFilters: { type: Function as PropType<(defaultQuery?: { [key: string]: any }) => unknown>, default: null },
   onCreateItem: { type: Function, default: null },
   onSetPage: { type: Function as PropType<(page: number) => unknown>, default: null },
   rootPage: { type: Boolean, default: false },
   uniqueKey: { type: String, default: 'id' },
   mode: { type: String as PropType<'default' | 'light'>, default: 'default' },
   loadingMore: { type: Boolean, default: false },
   onLazyLoad: { type: Function as PropType<() => void>, default: null },
   translation: {
      type: Function as PropType<(length: number) => string>,
      default: (length = 1) => i18n.global.t('generic.result', length || 1),
   },
   defaultQuery: { type: Object, default: () => ({}) },
   shortenHeadingLength: { type: Boolean, default: false },
});
const { onNavigateToDetails, onSetPage, onFilter, onResetFilters } = toRefs(props);

const headings = computed(() => props.headings.map((h, i) => ({ ...h, realIndex: i })));
//#endregion

//#region List Items
const hasActions = computed(() => !!slots['actionBarActions']);
const colCount = computed(() => (hasActions ? headings.value.length + 1 : headings.value.length));
//#endregion

//#region Paging
const route = useRoute();
const router = useRouter();

function setPage(page: number) {
   if (onSetPage.value) return onSetPage.value(page);
   return router.push({ ...route, query: { ...localQuery.value, page } });
}
//#endregion

//#region Sorting
const activeSort = computed(() => localQuery.value?.sort?.toString() || '');

function sortBy(params: { key: string; order: 'asc' | 'desc' }) {
   const { key, order } = params;
   router.push({ ...route, query: { ...localQuery.value, sort: `${order === 'asc' ? '' : '-'}${key}` } });
}

//#endregion

//#region Action Bar
const selectedItems = ref<Array<any>>([]);

const listItemIds = computed(() => (props.listItems ?? []).map((li) => getPropertyByKey(props.uniqueKey, li)));
const selectedItemIds = computed(() => (selectedItems.value ?? []).map((si) => getPropertyByKey(props.uniqueKey, si)));

const allItemsSelected = computed({
   get: () => {
      const hasAny = listItemIds.value.length > 0;
      const hasAllCurrent = listItemIds.value.every((li) => selectedItemIds.value.includes(li));
      return hasAny && hasAllCurrent;
   },
   set: (value: boolean) => {
      toggleSelectAllItems(value);
      return value;
   },
});

function selectItem(item: any) {
   const index = selectedItems.value.findIndex((si) => getPropertyByKey(props.uniqueKey, si) === getPropertyByKey(props.uniqueKey, item));
   if (index > -1) return selectedItems.value.splice(index, 1);
   return selectedItems.value.push(item);
}

function selectAllItems() {
   selectedItems.value = uniqueArray([...selectedItems.value, ...props.listItems], 'id');
}

function deselectAllItems() {
   selectedItems.value = [];
}

function toggleSelectAllItems(selectAll: boolean) {
   if (selectAll) return selectAllItems();
   return deselectAllItems();
}
//#endregion

//#region Create Item
const showCreateItemModal = ref(false);

function openCreateItemModal() {
   showCreateItemModal.value = true;
}
function closeCreateItemModal() {
   showCreateItemModal.value = false;
   showActionsList.value = false;
}
//#endregion

//#region Change Item
const showChangeItemModal = ref(false);
const itemToChange = ref<any>();

function openChangeItemModal(item: any) {
   showChangeItemModal.value = true;
   itemToChange.value = item;
}

function closeChangeItemModal() {
   showChangeItemModal.value = false;
   itemToChange.value = null;
}
//#endregion

//#region Filters
const debounce = ref();
const localQuery = computed(() => props.query || route.query);
const filters = ref({ ...localQuery.value });

const filterCount = computed(() => Object.keys(filters.value).filter((key) => !['page', 'per_page'].includes(key)).length);

function queryChanged(query: { [key: string]: string }) {
   if (deepEqual(query, filters.value)) return;
   filters.value = { ...query };
}

watch(localQuery, queryChanged, { immediate: true });

function filter({ key, value }: { key: string; value: string }) {
   if (debounce.value) clearTimeout(debounce.value);
   filters.value = { ...filters.value, [key]: value };

   debounce.value = setTimeout(() => {
      if (onFilter.value) return onFilter.value({ key, value });
      return router.push({ ...route, query: { ...filters.value, [key]: value, page: '1' } });
   }, 300);
}
function resetFilters() {
   if (onResetFilters.value) return onResetFilters.value(props.defaultQuery);
   else {
      filters.value = { ...props.defaultQuery };
      router.push({ ...route, query: { ...props.defaultQuery, page: 1 } });
   }
}
//#endregion

//#region Headings Filter
const genericListRef = ref<HTMLElement>();
const visibleHeadings = computed(() =>
   headings.value.map((h, i) => ({ ...h, realIndex: i })).filter((h) => (h.optional || h.temp_optional ? h.enabled : true)),
);

function toggleHeading(params: { index: number; value: boolean; refresh?: boolean }) {
   const { index, value, refresh } = params;

   let newHeadings = [...headings.value];
   newHeadings[index].enabled = value;
   newHeadings[index].temp_optional = !newHeadings[index].temp_optional;
   if (refresh) document.body.click();
   emits('update:headings', newHeadings);

   updateStickyHeadings();
}
//#endregion

//#region Pin Heading
async function pinHeading(params: { index: number; value: boolean }) {
   const { index, value: pin } = params;
   let newHeadings = [...headings.value];

   //update left
   const left = [...Array.from(genericListRef.value?.querySelectorAll('th.-pinned') || [])].reduce((acc, curr, i) => {
      if (i < index) return acc + curr.clientWidth;
      return acc;
   }, 0);
   newHeadings[index] = {
      ...newHeadings[index],
      style: { ...newHeadings[index].style, left: pin ? `${left}px` : 'unset', zIndex: pin ? 11 : 10 },
      pinned: pin,
      originalIndex: newHeadings[index].originalIndex === undefined ? index : newHeadings[index].originalIndex,
   };

   //update order
   const pinnedHeadings = [...newHeadings].filter((h) => h.pinned);
   const unpinnedHeadings = [...newHeadings]
      .filter((h) => !h.pinned)
      .sort((a, b) => {
         const aIndex = a.originalIndex !== undefined ? a.originalIndex : newHeadings.indexOf(a);
         const bIndex = b.originalIndex !== undefined ? b.originalIndex : newHeadings.indexOf(b);
         return aIndex - bIndex;
      });

   newHeadings = [...pinnedHeadings, ...unpinnedHeadings];

   emits('update:headings', newHeadings);
}

async function updateStickyHeadings() {
   await nextTick();
   if (!genericListRef.value) return;
   let newHeadings = [...headings.value];

   newHeadings = newHeadings.map((h, index) => {
      if (!h.label) return h;

      if (h.pinned) {
         const left = [...Array.from(genericListRef.value?.querySelectorAll('th.-pinned') || [])].reduce((acc, curr, i) => {
            if (i <= index) return acc + curr.clientWidth;
            return acc;
         }, 0);

         return {
            ...h,
            style: { ...h.style, left: `${left}px`, zIndex: 11 },
         };
      }
      return { ...h, style: { ...h.style, left: 'unset', zIndex: 10 } };
   });

   emits('update:headings', newHeadings);
}

const toggleTimeout = ref<NodeJS.Timeout>();
const timeoutIndex = ref<number>(-1);

function toggleShowMenuIcon(params: { index: number; value: boolean }) {
   const { index, value } = params;
   if (timeoutIndex.value === index && toggleTimeout.value) clearTimeout(toggleTimeout.value);

   timeoutIndex.value = index;

   toggleTimeout.value = setTimeout(
      () => {
         let newHeadings = [...headings.value];
         newHeadings[index].showMenuIcon = value;
         emits('update:headings', newHeadings);
      },
      value ? 0 : 500,
   );
}

onMounted(updateStickyHeadings);
onMounted(() => {
   window.addEventListener('resize', updateStickyHeadings);
});

onUnmounted(() => {
   window.removeEventListener('resize', updateStickyHeadings);
});
//#endregion

//#region Lazy Loading
const lazyLoadContainerRef = ref<HTMLElement | null>(null);

function handleLazyLoad() {
   if (props.loadingMore || props.loading) return;
   if (!props.onLazyLoad) return;

   const container = lazyLoadContainerRef.value;
   if (!container) return;

   const { scrollTop, scrollHeight, clientHeight } = container;
   const threshold = 100; // Load more when 100px from bottom

   if (scrollTop + clientHeight >= scrollHeight - threshold) {
      props.onLazyLoad();
   }
}

function setupScrollListener() {
   const container = genericListRef.value || null;
   if (container === lazyLoadContainerRef.value) return;

   removeScrollListener();
   lazyLoadContainerRef.value = container;
   if (!lazyLoadContainerRef.value) return;

   lazyLoadContainerRef.value.addEventListener('scroll', handleLazyLoad, { passive: true });
}

function removeScrollListener() {
   if (!lazyLoadContainerRef.value) return;
   lazyLoadContainerRef.value.removeEventListener('scroll', handleLazyLoad);
   lazyLoadContainerRef.value = null;
}

onMounted(() => {
   nextTick(() => {
      setupScrollListener();
   });
});

onUnmounted(() => {
   removeScrollListener();
});

watch([() => props.listItems.length, () => props.onLazyLoad, () => genericListRef.value], () => {
   nextTick(() => {
      setupScrollListener();
   });
});

//#region Dragging headings
const dragEnterIndex = ref<number>(-1);
const draggingIndex = ref<number>(-1);
const isDragging = ref(false);

function onDragStart(params: { event: DragEvent; index: number }) {
   const { event, index } = params;

   if (!props.advancedHeadings) return;
   isDragging.value = true;
   draggingIndex.value = index;
}

function onDragEnd() {
   if (!props.advancedHeadings) return;

   if (dragEnterIndex.value > -1 && draggingIndex.value > -1) {
      const newHeadings = [...headings.value];
      const heading = newHeadings.splice(draggingIndex.value, 1)[0];
      newHeadings.splice(dragEnterIndex.value, 0, heading);
      emits('update:headings', newHeadings);
   }

   isDragging.value = false;
   dragEnterIndex.value = -1;
   draggingIndex.value = -1;
}

function onDragEnter(params: { event: DragEvent; index: number }) {
   const { event, index } = params;
   if (!props.advancedHeadings) return;
   dragEnterIndex.value = index;
}

//#endregion

//#region Click list item
function clickListItem(item: any) {
   if (slots['changeModal']) {
      return openChangeItemModal(item);
   }

   if (onNavigateToDetails.value) onNavigateToDetails.value(item);
}
//#endregion

//#region Actions
const showActionsList = ref(false);

function triggerActions() {
   if (slots['actionsList']) {
      return (showActionsList.value = !showActionsList.value);
   }
   if (props.onCreateItem) {
      return props.onCreateItem();
   }
   return (showCreateItemModal.value = true);
}

function closeActionsList() {
   showActionsList.value = false;
}
//#endregion

//#region Toggle Filter Panel
const showFilterPanel = ref(false);

function toggleFilterPanel() {
   if (!slots['filterPanel']) return;

   showFilterPanel.value = !showFilterPanel.value;
}
//#endregion
</script>

<template>
   <div
      :class="[
         'generic-list',
         {
            '-with-filters': !!slots['filters'],
            '-root': rootPage,
            '-with-warning-message': !!slots['warningMessage'],
            '-advanced-headings': advancedHeadings,
            '-dragging': isDragging,
         },
      ]"
   >
      <!-- Filters -->
      <GenericListFilters
         v-if="$slots.filters"
         :filters="filters"
         :has-reset-filters-button="hasResetFiltersButton"
         :headings="headings"
         :mode
         @filter="filter"
         @reset-filters="resetFilters"
         @toggle-heading="toggleHeading"
      >
         <template #filters="filterBindings">
            <slot name="filters" v-bind="filterBindings"></slot>
         </template>

         <template #filterAddons>
            <slot name="filterAddons"></slot>
         </template>
      </GenericListFilters>

      <!-- Warning Message -->
      <div v-if="slots['warningMessage']" class="generic-list__warning-message">
         <slot name="warningMessage"></slot>
      </div>

      <!-- List -->
      <div ref="genericListRef" class="generic-list__list">
         <AsyncNxtTable
            :count="meta?.total || listItems.length || 0"
            :fixed-heading="true"
            :mode
            :hover-effect="!!slots['changeModal'] || !!onNavigateToDetails"
            :loading-col-count="visibleHeadings.length + (hasActions ? 1 : 0)"
            :loading-row-count="loadingRowCount"
            :loading="loading"
         >
            <template #head>
               <GenericListHeadings
                  v-model:all-items-selected="allItemsSelected"
                  :name="name"
                  :has-actions="hasActions"
                  :loading="loading"
                  :visible-headings="visibleHeadings"
                  :advanced-headings="advancedHeadings"
                  :active-sort="activeSort"
                  :is-dragging="isDragging"
                  :drag-enter-index="dragEnterIndex"
                  :shorten-heading-length="shortenHeadingLength"
                  @sort-by="sortBy"
                  @drag-start="onDragStart"
                  @drag-end="onDragEnd"
                  @drag-enter="onDragEnter"
                  @toggle-show-menu-icon="toggleShowMenuIcon"
                  @pin-heading="pinHeading"
                  @toggle-heading="toggleHeading"
               >
                  <template #headings="headingBindings">
                     <slot name="headings" v-bind="{ ...headingBindings, toggleFilterPanel, filterCount }"></slot>
                  </template>
               </GenericListHeadings>
            </template>

            <template #noResults>{{ $t('generic.feedback.no-results-for-type', { type: translation(2)?.toLowerCase() }, 2) }}</template>
            <template #body>
               <slot v-if="slots.body" name="body" v-bind="{ listItems, showChangeItemModal, selectedItems, selectItem, clickListItem }">
               </slot>

               <template v-else>
                  <GenericListItem
                     v-for="(item, index) in listItems"
                     :key="item.id"
                     :item="item"
                     :has-actions="hasActions"
                     :name="`${name}-${getPropertyByKey(uniqueKey, item)}`"
                     :get-is-selectable="getIsSelectable"
                     :is-selected="!!selectedItems.find((si) => getPropertyByKey(uniqueKey, si) === getPropertyByKey(uniqueKey, item))"
                     :advanced-headings="advancedHeadings"
                     :visible-headings="visibleHeadings"
                     :is-dragging="isDragging"
                     :drag-enter-index="dragEnterIndex"
                     @select-item="selectItem"
                     @drag-enter="onDragEnter"
                     @click="getIsSelectable ? (getIsSelectable(item) ? clickListItem(item) : null) : clickListItem(item)"
                  >
                     <template #listItem="listItemBinding">
                        <slot name="listItem" v-bind="{ ...listItemBinding, index, showChangeItemModal }"></slot>
                     </template>

                     <template v-for="(_, slotName) in $slots" v-slot:[slotName]="slotProps">
                        <slot :name="slotName" v-bind="slotProps"></slot>
                     </template>
                  </GenericListItem>
               </template>

               <!-- Loading more indicator -->
               <tr v-if="loadingMore && !loading">
                  <td :colspan="visibleHeadings.length + (hasActions ? 1 : 0)" class="text-center">
                     <div class="d-flex flex-row align-items-center gap-4 justify-content-center">
                        <NxtSpinner size="small" />
                        {{ $t('generic.feedback.loading') }}...
                     </div>
                  </td>
               </tr>
            </template>
         </AsyncNxtTable>
      </div>

      <!-- Paging -->
      <div v-if="hasPaging" class="generic-list__paging">
         <NxtPaging
            :current_page="!!onSetPage ? meta?.current_page : +(localQuery.page || meta?.current_page || 1)"
            :total="meta?.total || 0"
            :per_page="!!onSetPage ? meta?.per_page : +(localQuery.per_page || meta?.per_page || 25)"
            @on-set-page="setPage"
         />
      </div>

      <!-- Action Bar -->
      <Transition name="fade">
         <NxtActionBar
            v-if="selectedItems.length > 0"
            class="generic-list__action-bar"
            :count="selectedItems.length"
            :count-type="t('generic.selected')"
            :disabled="loading"
            style="font-size: var(--nxt-font-large)"
            @on-close="deselectAllItems"
         >
            <div class="d-flex gap-3 align-items-center me-3">
               <slot name="actionBarActions" v-bind="{ selectedItems, deselectAllItems, count: selectedItems?.length || 0 }"></slot>
            </div>
         </NxtActionBar>
      </Transition>

      <!-- Actions -->
      <div class="generic-list__actions">
         <Transition name="fade">
            <div v-if="showActionsList" class="generic-list__actions-list">
               <slot name="actionsList" v-bind="{ closeActionsList, openCreateItemModal }"></slot>
            </div>
         </Transition>
         <NxtActionButton
            v-if="slots['createModal'] || !!onCreateItem || slots['actionsList']"
            :class="['generic-list__action-button', { '-creating': showCreateItemModal || showActionsList }]"
            @on-click="triggerActions"
         >
            <slot name="actionButtonIcon">
               <i class="fas fa-plus"></i>
            </slot>
         </NxtActionButton>
      </div>

      <!-- Create Item -->
      <slot v-if="showCreateItemModal" name="createModal" v-bind="{ closeCreateItemModal }"></slot>

      <!-- Change Item -->
      <slot v-if="showChangeItemModal" name="changeModal" v-bind="{ item: itemToChange, closeChangeItemModal }"></slot>

      <!-- Filter Panel -->
      <slot
         v-if="showFilterPanel"
         name="filterPanel"
         v-bind="{
            initialFilters: filters,
            onClose: () => (showFilterPanel = false),
         }"
      ></slot>

      <!-- Additional Modals or Info -->
      <slot></slot>
   </div>
</template>

<style lang="scss">
.generic-list {
   height: 100%;
   overflow: auto;
   display: grid;
   grid-template-columns: 1fr;
   grid-template-rows: 1fr auto;
   position: relative;

   &.-with-filters {
      grid-template-rows: auto 1fr auto;

      &.-with-warning-message {
         grid-template-rows: auto auto 1fr auto;
      }
   }

   &.-with-warning-message {
      grid-template-rows: auto 1fr toRem(48);
   }

   &.-root {
      .generic-list__filters {
         padding-bottom: var(--nxt-gutter);
         border-bottom: toRem(1) solid var(--nxt-grey-200);
      }
   }

   &.-advanced-headings {
      th {
         cursor: grab;
      }

      th:hover {
         background-color: var(--surface-base-0);
         border-radius: var(--nxt-radius);
      }

      &.-dragging {
         th {
            border-radius: var(--nxt-radius);
            cursor: grabbing;
         }
      }

      td.-drag-entered,
      th.-drag-entered {
         position: relative;
         &::before {
            content: '';
            position: absolute;
            height: calc(100% + toRem(4));
            left: toRem(-3);
            width: toRem(3);
            z-index: 9999;
            top: 0;
            background-color: var(--state-success);
         }
      }
   }
}

.generic-list__warning-message {
   margin: var(--nxt-gutter-large) 0;
   margin-right: var(--nxt-gutter-small);
}

.generic-list__list {
   table-layout: fixed;
   overflow: auto;

   td {
      padding: v-bind(listItemPadding);
      min-height: toRem(40);
   }

   td,
   th {
      &.-pinned {
         position: sticky;
      }
   }

   .nxt-table.-loading td {
      height: toRem(42);
      background-color: var(--surface-base-0);
   }

   .nxt-icon-button {
      margin: var(--nxt-gutter--negative) 0;
   }
}

.generic-list__paging {
   background-color: var(--surface-base-0);
   width: 100%;
   padding-top: var(--nxt-gutter);
   position: sticky;
   bottom: 0;
   height: fit-content;

   display: flex;
   align-items: center;
   justify-content: center;
}

.generic-list__action-bar {
   position: fixed;
   bottom: 5rem;
   left: calc(50%);
   transform: translateX(-50%);
   z-index: var(--zindex-sticky);
   min-width: toRem(400);
}

.generic-list__actions {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
   align-items: flex-end;
   z-index: var(--zindex-sticky);
   position: fixed;
   right: toRem(32);
   bottom: toRem(32);

   @include md {
      bottom: toRem(108);
   }
}

.generic-list__action-button {
   font-size: toRem(16);
   transition: transform 0.2s ease-in-out;

   &.-creating {
      transform: rotate(45deg);
   }
}

.generic-list__actions-list {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter-small);
   align-items: flex-end;
}
</style>
